import type { ChatMessagePart } from '../types';

interface ToolState {
    toolCallId: string;
    toolName: string;
    args: Record<string, unknown>;
    status: string;
}

interface AdapterState {
    textAccumulator: string;
    toolStates: Map<string, ToolState>;
    parts: ChatMessagePart[];
    activeItems: Map<string, { kind: string; partIndex: number }>;
}

interface StreamEvent {
    type: string;
    itemId?: string;
    data?: Record<string, unknown>;
    [key: string]: unknown;
}

export function createAdapterState(): AdapterState {
    return {
        textAccumulator: '',
        toolStates: new Map(),
        parts: [],
        activeItems: new Map(),
    };
}

export function adaptStreamEvent(
    event: StreamEvent,
    state: AdapterState,
): ChatMessagePart[] {
    const itemId = event.itemId ?? (event.data?.itemId as string | undefined) ?? '';

    switch (event.type) {
        case 'run.started':
            break;

        case 'item.created': {
            const kind = event.data?.kind as string | undefined;
            switch (kind) {
                case 'reasoning': {
                    const summaryParts = event.data?.summaryParts as Array<{ kind: string; text?: string }> | undefined;
                    const text = summaryParts?.map((p) => p.text ?? '').join('') ?? '';
                    state.parts.push({ type: 'reasoning', text });
                    state.activeItems.set(itemId, { kind: 'reasoning', partIndex: state.parts.length - 1 });
                    break;
                }
                case 'tool_call': {
                    const toolCallId = itemId;
                    const toolName = (event.data?.toolName as string) ?? (event.data?.tool as string) ?? '';
                    const args = (event.data?.args as Record<string, unknown>) ?? {};
                    state.toolStates.set(toolCallId, { toolCallId, toolName, args, status: 'executing' });
                    state.parts.push({
                        type: 'tool-call',
                        toolCallId,
                        toolName,
                        args,
                        state: 'input-available',
                    });
                    state.activeItems.set(itemId, { kind: 'tool_call', partIndex: state.parts.length - 1 });
                    break;
                }
                case 'text': {
                    state.activeItems.set(itemId, { kind: 'text', partIndex: -1 });
                    break;
                }
                case 'state': {
                    const stateName = (event.data?.stateName as string) ?? '';
                    const stateType = (event.data?.stateType as string) ?? '';
                    const label = stateType ? `${stateType}: ${stateName}` : stateName;
                    state.parts.push({ type: 'status', message: `Step: ${label}`, state: 'info' });
                    state.activeItems.set(itemId, { kind: 'state', partIndex: state.parts.length - 1 });
                    break;
                }
                case 'task': {
                    const resource = (event.data?.resource as string) ?? '';
                    state.parts.push({ type: 'status', message: `Running ${resource}...`, state: 'info' });
                    state.activeItems.set(itemId, { kind: 'task', partIndex: state.parts.length - 1 });
                    break;
                }
                case 'choice': {
                    const matched = (event.data?.matched as string) ?? '';
                    state.parts.push({ type: 'status', message: `Branch: ${matched}`, state: 'info' });
                    break;
                }
                case 'parallel': {
                    const branchCount = (event.data?.branchCount as number) ?? 0;
                    state.parts.push({ type: 'status', message: `Running ${branchCount} parallel branches`, state: 'info' });
                    break;
                }
                case 'map': {
                    const itemCount = (event.data?.itemCount as number) ?? 0;
                    state.parts.push({ type: 'status', message: `Processing ${itemCount} items`, state: 'info' });
                    break;
                }
                case 'progress': {
                    const message = (event.data?.message as string) ?? '';
                    const percent = event.data?.percent as number | undefined;
                    state.parts.push({ type: 'status', message, state: 'info', progress: percent });
                    break;
                }
                case 'message': {
                    state.activeItems.set(itemId, { kind: 'message', partIndex: -1 });
                    break;
                }
                default:
                    break;
            }
            break;
        }

        case 'item.delta': {
            const appendParts = event.data?.append as Array<{ kind: string; text?: string; language?: string; code?: string }> | undefined;
            if (appendParts) {
                for (const part of appendParts) {
                    if (part.kind === 'text' || part.kind === 'markdown') {
                        state.textAccumulator += part.text ?? '';
                        const textIdx = state.parts.findIndex((p) => p.type === 'text');
                        if (textIdx >= 0) {
                            state.parts[textIdx] = { type: 'text', text: state.textAccumulator };
                        } else {
                            state.parts.push({ type: 'text', text: state.textAccumulator });
                        }
                    } else if (part.kind === 'code') {
                        state.parts.push({ type: 'code', language: part.language ?? 'text', code: part.code ?? '' });
                    }
                }
            }
            break;
        }

        case 'item.patch': {
            const status = event.data?.status as string | undefined;
            const result = event.data?.result;
            const error = event.data?.error as string | undefined;

            if (status === 'completed' && result !== undefined) {
                const tc = state.toolStates.get(itemId);
                if (tc) tc.status = 'completed';
                state.parts.push({
                    type: 'tool-result',
                    toolCallId: itemId,
                    toolName: tc?.toolName ?? '',
                    result,
                });
            } else if (status === 'failed' || error) {
                const tc = state.toolStates.get(itemId);
                if (tc) tc.status = 'error';
                state.parts.push({
                    type: 'tool-result',
                    toolCallId: itemId,
                    toolName: tc?.toolName ?? '',
                    result: null,
                    error: error ?? 'Unknown error',
                });
            }
            break;
        }

        case 'item.completed': {
            const active = state.activeItems.get(itemId);
            if (active) {
                const status = (event.data?.status as string) ?? 'completed';
                const durationMs = event.data?.duration_ms as number | undefined;
                if ((active.kind === 'state' || active.kind === 'task') && active.partIndex >= 0) {
                    const existing = state.parts[active.partIndex];
                    if (existing && existing.type === 'status') {
                        const suffix = durationMs != null ? ` (${durationMs}ms)` : '';
                        state.parts[active.partIndex] = {
                            ...existing,
                            state: status === 'failed' ? 'error' : 'success',
                            message: existing.message + suffix,
                        };
                    }
                }
                state.activeItems.delete(itemId);
            }
            break;
        }

        case 'notice.created': {
            const level = (event.data?.level as string) ?? 'info';
            const message = (event.data?.message as string) ?? '';
            const mappedState = level === 'error' ? 'error' : level === 'warning' ? 'warning' : 'info';
            state.parts.push({ type: 'status', message, state: mappedState as 'info' | 'warning' | 'error' });
            break;
        }

        case 'run.completed': {
            const output = event.data?.output as Record<string, unknown> | undefined;
            if (output) {
                let content = output.content as string | undefined;
                if (content == null && output.message && typeof output.message === 'object') {
                    content = (output.message as Record<string, unknown>).content as string | undefined;
                }
                if (typeof content === 'string' && content.length > 0 && state.textAccumulator.length === 0) {
                    state.parts.push({ type: 'text', text: content });
                    state.textAccumulator = content;
                } else if (state.textAccumulator.length === 0) {
                    const serialized = JSON.stringify(output, null, 2);
                    if (serialized !== '{}') {
                        state.parts.push({ type: 'text', text: serialized });
                    }
                }
            }
            break;
        }

        case 'result': {
            const data = event.data as Record<string, unknown> | undefined;
            if (!data) break;
            let content: string | undefined;
            if (typeof data.content === 'string') {
                content = data.content;
            } else if (data.message && typeof data.message === 'object') {
                content = (data.message as Record<string, unknown>).content as string | undefined;
            } else if (typeof data.text === 'string') {
                content = data.text;
            }
            if (Array.isArray(data.parts)) {
                for (const p of data.parts as ChatMessagePart[]) {
                    state.parts.push(p);
                }
            } else if (typeof content === 'string' && content.length > 0) {
                state.parts.push({ type: 'text', text: content });
                state.textAccumulator = content;
            } else if (state.textAccumulator.length === 0) {
                const serialized = JSON.stringify(data, null, 2);
                if (serialized !== '{}') {
                    state.parts.push({ type: 'text', text: serialized });
                }
            }
            break;
        }

        case 'error': {
            const data = event.data as Record<string, unknown> | undefined;
            const code = (data?.code as string) ?? 'GATEWAY_ERROR';
            const message = (data?.message as string) ?? 'An error occurred';
            state.parts.push({ type: 'error', code, message });
            break;
        }

        case 'run.failed': {
            const raw = event.data?.error;
            let msg = 'Invocation failed';
            if (typeof raw === 'string') msg = raw;
            else if (raw && typeof raw === 'object') {
                const obj = raw as Record<string, unknown>;
                msg = (typeof obj.message === 'string' ? obj.message : '') || msg;
            }
            state.parts.push({ type: 'error', code: 'RUN_FAILED', message: msg });
            break;
        }

        case 'run.cancelled':
            state.parts.push({ type: 'status', message: 'Generation cancelled', state: 'info' });
            break;

        case 'completed':
        case 'heartbeat':
            break;
    }

    return [...state.parts];
}

export function isTerminalEvent(event: StreamEvent): boolean {
    if (event.type === 'run.completed' || event.type === 'run.failed' || event.type === 'run.cancelled') return true;
    if (event.type === 'result' || event.type === 'error' || event.type === 'completed') return true;
    if ((event as Record<string, unknown>).terminal === true) return true;
    return false;
}
